import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '../lib/utils';

/**
 * The value currently selected in the enclosing `Tabs`, mirrored so
 * `TabsTrigger` can tell whether its panel is in the document.
 *
 * WHY THIS EXISTS — the dangling `aria-controls` (WCAG 4.1.2)
 * -----------------------------------------------------------
 * Radix stamps every trigger with `aria-controls="<baseId>-content-<value>"`
 * unconditionally, and unmounts `Tabs.Content` for every value that is not
 * selected. On any tab strip with more than one tab that leaves N-1 triggers
 * pointing at element ids that do not exist, which axe-core reports as
 * `aria-valid-attr-value` at CRITICAL impact — and a screen reader following
 * the reference lands nowhere. The 2026-07-29 a11y sweep found ten such nodes
 * across the alarms console, the finance close cockpit and the close wizard;
 * the trigger ids in the report (`#radix-_r_17_-trigger-checklist`) are all
 * Radix tab triggers.
 *
 * The alternative fix — `forceMount` on every `Tabs.Content` — keeps the panel
 * in the document but ALSO renders the children of every unselected tab
 * (Radix only gates `children` on `present`, and `present` is forced true), so
 * every chart and table behind every tab would mount on first paint. That is a
 * cross-cutting runtime cost paid on every route to satisfy a reference nobody
 * needs while the panel is absent.
 *
 * `aria-controls` is NOT a required attribute of `role="tab"` (axe's
 * `aria-required-attr` does not ask for it), so the correct reading is: point
 * at the panel when the panel is there, and say nothing when it is not.
 */
const TabsValueContext = React.createContext<string | undefined>(undefined);

type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

const Tabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ value, defaultValue, onValueChange, ...props }, ref) => {
    // Mirrors the uncontrolled value; the controlled `value` always wins so a
    // consumer driving the strip from outside never disagrees with the mirror.
    const [uncontrolled, setUncontrolled] = React.useState<string | undefined>(defaultValue);
    const selected = value ?? uncontrolled;

    const handleValueChange = React.useCallback(
      (next: string) => {
        setUncontrolled(next);
        onValueChange?.(next);
      },
      [onValueChange]
    );

    return (
      <TabsValueContext.Provider value={selected}>
        <TabsPrimitive.Root
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          {...props}
        />
      </TabsValueContext.Provider>
    );
  }
);
Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Height follows the density control token so the tab strip resizes
      // across comfortable/compact/touch/wall from --space-base.
      // `max-w-full overflow-x-auto` lets a long tab strip scroll horizontally
      // on narrow (phone/tablet) viewports instead of clipping or forcing the
      // page wider than the screen. Shells that pass `flex-wrap` keep wrapping;
      // the two are compatible.
      'inline-flex h-[var(--control-height-md)] max-w-full items-center justify-center overflow-x-auto rounded-full bg-surface-sunken p-1 text-muted-foreground',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, value, ...props }, ref) => {
  const selected = React.useContext(TabsValueContext);
  const panelIsMounted = selected !== undefined && selected === value;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-full px-[var(--control-padding-x-md)] py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-surface-raised data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        className
      )}
      // Radix spreads consumer props AFTER its own attributes, so an explicit
      // `undefined` here REMOVES the reference rather than merely failing to
      // add it. Only emitted while the panel is absent from the document.
      {...(panelIsMounted ? {} : { 'aria-controls': undefined })}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
